import React, { useEffect, useRef, useState } from 'react';
import { Button, Space, Typography, Badge, Spin, Alert, Modal, Tag } from 'antd';
import { 
  AudioOutlined,
  AudioMutedOutlined,
  VideoCameraOutlined,
  VideoCameraOutlined as VideoCameraOffOutlined,
  CloseOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  PlayCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
// Remove top-level import of agora-rtc-sdk-ng
// import AgoraRTC, { IAgoraRTCClient, IAgoraRTCRemoteUser, ILocalAudioTrack, ILocalVideoTrack } from 'agora-rtc-sdk-ng';

// Dynamic import types - will be available only on client side
type AgoraRTC = any;
type IAgoraRTCClient = any;
type IAgoraRTCRemoteUser = any;
type ILocalAudioTrack = any;
type ILocalVideoTrack = any;

const { Title, Text } = Typography;

const PublicAgoraCall: React.FC = () => {
  const [isInCall, setIsInCall] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [finalCallDuration, setFinalCallDuration] = useState(0);
  const [showStartCallButton, setShowStartCallButton] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [paramsError, setParamsError] = useState<string | null>(null);
  const [showEndCallModal, setShowEndCallModal] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const callTimerRef = useRef<NodeJS.Timeout>();
  const agoraClientRef = useRef<IAgoraRTCClient | null>(null);
  const localTracksRef = useRef<{ audioTrack?: ILocalAudioTrack; videoTrack?: ILocalVideoTrack }>({});

  // Read query params
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const appId = searchParams.get('appid') || '';
  const channel = searchParams.get('channel') || '';
  const uid = searchParams.get('uid') || '';
  const role = (searchParams.get('role') as 'publisher' | 'subscriber') || 'publisher';
  const token = searchParams.get('token') || '';
  const appointmentId = searchParams.get('appointmentId') || '';

  // Validate params
  useEffect(() => {
    if (!appId || !channel || !uid || !token) {
      setParamsError('Missing required parameters. Please ensure appid, channel, uid, and token are provided in the URL.');
    }
  }, [appId, channel, uid, token]);

  // Initialize Agora client
  useEffect(() => {
    const initClient = async () => {
      if (!agoraClientRef.current && typeof window !== 'undefined') {
        try {
          const { default: AgoraRTC } = await import('agora-rtc-sdk-ng');
          agoraClientRef.current = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        } catch (error) {
          console.error('Failed to initialize Agora client:', error);
        }
      }
    };

    initClient();

    return () => {
      if (agoraClientRef.current) {
        agoraClientRef.current.leave();
      }
    };
  }, []);

  // Event listeners
  useEffect(() => {
    if (!agoraClientRef.current) return;

    const client = agoraClientRef.current;

    const handleUserPublished = async (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
      await client.subscribe(user, mediaType);
      if (mediaType === 'video') {
        setRemoteUsers(prev => {
          const exists = prev.find(u => u.uid === user.uid);
          if (exists) return prev.map(u => (u.uid === user.uid ? user : u));
          return [...prev, user];
        });
      } else if (mediaType === 'audio') {
        // Ensure remote audio plays on receiver
        try {
          user.audioTrack?.play();
        } catch (e) {
          console.warn('Failed to play remote audio track:', e);
        }
      }
    };

    const handleUserUnpublished = (user: IAgoraRTCRemoteUser) => {
      setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
    };

    const handleUserJoined = (user: IAgoraRTCRemoteUser) => {
      setRemoteUsers(prev => [...prev, user]);
    };

    const handleUserLeft = (user: IAgoraRTCRemoteUser) => {
      setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
    };

    client.on('user-published', handleUserPublished);
    client.on('user-unpublished', handleUserUnpublished);
    client.on('user-joined', handleUserJoined);
    client.on('user-left', handleUserLeft);

    return () => {
      client.off('user-published', handleUserPublished);
      client.off('user-unpublished', handleUserUnpublished);
      client.off('user-joined', handleUserJoined);
      client.off('user-left', handleUserLeft);
    };
  }, []);

  // Render remote video
  useEffect(() => {
    if (remoteUsers.length > 0 && videoRef.current) {
      const remoteUser = remoteUsers[0];
      if (remoteUser.videoTrack) {
        remoteUser.videoTrack.play(videoRef.current);
      }
    }
  }, [remoteUsers]);

  // Render local video
  useEffect(() => {
    if (localTracksRef.current.videoTrack && localVideoRef.current) {
      localTracksRef.current.videoTrack.play(localVideoRef.current);
    }
  }, [localTracksRef.current.videoTrack]);

  useEffect(() => {
    if (isInCall) {
      callTimerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);
    }
    return () => {
      if (callTimerRef.current) clearInterval(callTimerRef.current);
    };
  }, [isInCall]);

  const handleStartCall = async () => {
    if (!agoraClientRef.current || paramsError) return;
    setIsConnecting(true);
    setShowStartCallButton(false);
    try {
      const client = agoraClientRef.current;
      await client.join(appId, channel, token, uid);
      
      // Dynamically import AgoraRTC for track creation
      const { default: AgoraRTC } = await import('agora-rtc-sdk-ng');
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
      localTracksRef.current = { audioTrack, videoTrack };
      await client.publish([audioTrack, videoTrack]);
      setIsInCall(true);
      setCallDuration(0);
      setFinalCallDuration(0);
    } catch (err) {
      console.error('Failed to start call:', err);
      setIsConnecting(false);
      setShowStartCallButton(true);
    } finally {
      setIsConnecting(false);
    }
  };

  const toggleAudio = async () => {
    const track = localTracksRef.current.audioTrack;
    if (!track) return;
    if (isAudioMuted) {
      await track.setEnabled(true);
    } else {
      await track.setEnabled(false);
    }
    setIsAudioMuted(!isAudioMuted);
  };

  const toggleVideo = async () => {
    const track = localTracksRef.current.videoTrack;
    if (!track) return;
    if (isVideoOff) {
      await track.setEnabled(true);
    } else {
      await track.setEnabled(false);
    }
    setIsVideoOff(!isVideoOff);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (videoRef.current) {
      if (!isFullscreen) {
        videoRef.current.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    }
  };

  const handleEndCall = () => {
    setFinalCallDuration(callDuration); // Store current duration before showing modal
    setShowEndCallModal(true);
  };

  const confirmEndCall = async () => {
    try {
      if (localTracksRef.current.audioTrack) localTracksRef.current.audioTrack.close();
      if (localTracksRef.current.videoTrack) localTracksRef.current.videoTrack.close();
      localTracksRef.current = {};
      if (agoraClientRef.current) await agoraClientRef.current.leave();
      setIsInCall(false);
      setFinalCallDuration(callDuration); // Store final duration
      setCallDuration(0);
      setRemoteUsers([]);
      setShowStartCallButton(true);
      setShowEndCallModal(false);
    } catch (err) {
      console.error('Error ending call:', err);
    }
  };

  const formatCallDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (paramsError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Invalid call link" description={paramsError} showIcon />
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#000' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', backgroundColor: 'rgba(0,0,0,0.8)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ color: 'white', margin: 0 }}>Video Consultation</Title>
          <Text style={{ color: 'rgba(255,255,255,0.8)' }}>{appointmentId ? `Appointment: ${appointmentId}` : `Channel: ${channel}`}</Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Badge status={isConnecting ? 'processing' : isInCall ? 'success' : 'default'} text={isConnecting ? 'Connecting' : isInCall ? 'Connected' : 'Disconnected'} />
          {isInCall && <Text style={{ color: 'white' }}>{formatCallDuration(callDuration)}</Text>}
          {remoteUsers.length > 0 && <Tag color="green">{remoteUsers.length} participants</Tag>}
        </div>
      </div>

      {/* Video Area */}
      <div style={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover', backgroundColor: '#1a1a1a' }} autoPlay playsInline />

          {showStartCallButton && !isInCall && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: 'white' }}>
              <Button type="primary" size="large" icon={<PlayCircleOutlined />} onClick={handleStartCall} loading={isConnecting} style={{ height: 80, width: 200, fontSize: 18, borderRadius: 40, backgroundColor: '#1890ff', border: 'none' }}>
                {isConnecting ? 'Connecting…' : 'Start Call'}
              </Button>
              <div style={{ marginTop: 16 }}>
                <Text style={{ color: 'white', fontSize: 16 }}>Click to start call</Text>
              </div>
            </div>
          )}

          {isInCall && remoteUsers.length === 0 && !showStartCallButton && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: 'white' }}>
              <UserOutlined style={{ fontSize: 64, opacity: 0.5 }} />
              <div style={{ marginTop: 16 }}>
                <Text style={{ color: 'white', fontSize: 16 }}>Waiting for participant…</Text>
              </div>
            </div>
          )}
        </div>

        {isInCall && localTracksRef.current.videoTrack && (
          <div style={{ position: 'absolute', top: 20, right: 20, width: 120, height: 90, borderRadius: 8, overflow: 'hidden', border: '2px solid white' }}>
            <video ref={localVideoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} autoPlay playsInline muted />
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={{ padding: '24px', backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Space size="large">
          <Button type="text" icon={isAudioMuted ? <AudioMutedOutlined /> : <AudioOutlined />} onClick={toggleAudio} disabled={!isInCall} style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: isAudioMuted ? '#ff4d4f' : '#1890ff', color: 'white', border: 'none' }} />
          <Button type="text" icon={isVideoOff ? <VideoCameraOffOutlined /> : <VideoCameraOutlined />} onClick={toggleVideo} disabled={!isInCall} style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: isVideoOff ? '#ff4d4f' : '#1890ff', color: 'white', border: 'none' }} />
          <Button type="text" icon={<CloseOutlined />} onClick={handleEndCall} style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#ff4d4f', color: 'white', border: 'none' }} />
          <Button type="text" icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={toggleFullscreen} style={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#1890ff', color: 'white', border: 'none' }} />
        </Space>
      </div>

      {/* End Call Modal */}
      <Modal title="End Call" open={showEndCallModal} onOk={confirmEndCall} onCancel={() => setShowEndCallModal(false)} okText="End Call" cancelText="Cancel" okButtonProps={{ danger: true }}>
        <p>Are you sure you want to end the call?</p>
        <p>Duration: {formatCallDuration(finalCallDuration)}</p>
      </Modal>
    </div>
  );
};

export default PublicAgoraCall; 